/**
 * @fullvsl/contracts/tenant-connection — Mock per-subaccount connection manager (FUL-128).
 *
 * Concrete no-credentials path for the self-service connection lifecycle. Each
 * subaccount admin "connects" their Full Funnel / HighLevel location inside
 * FullVSL by supplying an API key / private-integration token they control.
 *
 * This mock:
 *   - Hashes the secret immediately (SHA-256) and stores ONLY the hash + a
 *     display mask. The raw secret is never retained, logged, or returned.
 *   - Validates scopes against REQUIRED_FF_SCOPES on connect + test.
 *   - Records a connection lifecycle (pending → connected / error → revoked).
 *   - Enforces strict one-active-connection-per-location isolation.
 *   - Rotates by re-supplying a secret to an existing location (updates hash).
 *
 * Swap-in: a real `ConnectionManager` envelope-encrypts the secret at rest
 * (Cloudflare Workers Secret / KMS), proxies scoped GHL calls per location, and
 * calls `locations.read` + an OAuth introspect for the scope test. The
 * interface is identical; consumers never change.
 */

import type {
  ConnectRequest,
  ConnectionManager,
  ConnectionStatus,
  ConnectionTestRequest,
  ConnectionTestResult,
  TenantConnection,
} from "./contracts";
import { REQUIRED_FF_SCOPES } from "./contracts";

/* =====================================================================================
 * Fixture tenants / locations (QA / demo). Mock location ids + names.
 * ===================================================================================== */

export interface FixtureTenant {
  locationId: string;
  locationName: string;
  /** A mock API key the test harness uses to simulate a real admin connect. */
  mockSecret: string;
  /** Whether this fixture credential passes the scope check (for error-path QA). */
  scopesOk: boolean;
}

/**
 * QA fixture tenants. `connect()` with `FIXTURE_TENANTS[0].mockSecret` succeeds;
 * `[1]` fails scope check (drives the "insufficient scopes" error path); `[2]`
 * is a second valid location for multi-tenant isolation tests.
 */
export const FIXTURE_TENANTS: FixtureTenant[] = [
  {
    locationId: "loc_demo_acme",
    locationName: "Acme Corp (Demo Location)",
    mockSecret: "pit-demo-acme-1111-2222-3333",
    scopesOk: true,
  },
  {
    locationId: "loc_demo_limited",
    locationName: "Limited Scopes Inc (Error Fixture)",
    mockSecret: "pit-demo-limited-0000",
    scopesOk: false,
  },
  {
    locationId: "loc_demo_beta",
    locationName: "Beta VSL (Second Valid Location)",
    mockSecret: "pit-demo-beta-4444-5555",
    scopesOk: true,
  },
];

/** Resolve a fixture tenant by its mock secret (simulates GHL token introspect). */
export function fixtureTenantBySecret(secret: string): FixtureTenant | undefined {
  return FIXTURE_TENANTS.find((t) => t.mockSecret === secret);
}

/* =====================================================================================
 * Credential masking + hashing helpers (isomorphic Web Crypto).
 * ===================================================================================== */

/** SHA-256 hex hash of the raw secret (stable for the same value). */
export async function hashSecret(secret: string): Promise<string> {
  const enc = new TextEncoder();
  const digest = await globalThis.crypto.subtle.digest("SHA-256", enc.encode(secret));
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** Display-safe mask: keep first 4 + last 4 chars, redact the middle. */
export function maskSecret(secret: string): string {
  if (secret.length <= 8) return "•".repeat(secret.length);
  return `${secret.slice(0, 4)}${"•".repeat(Math.min(secret.length - 8, 20))}${secret.slice(-4)}`;
}

/* =====================================================================================
 * MockConnectionManager — in-memory, no-credentials connection lifecycle.
 * ===================================================================================== */

/**
 * In-memory `ConnectionManager`. The secret is hashed on connect and discarded;
 * only `RedactedCredential` (mask + hash) is retained. A real manager stores the
 * encrypted blob behind the same interface.
 *
 * Scope simulation: `FIXTURE_TENANTS` maps a known secret to a scope outcome. An
 * unknown secret is treated as "valid with full scopes" (so any arbitrary key the
 * admin types is accepted in mock mode, simulating a real token introspect pass).
 * Use `FIXTURE_TENANTS[1].mockSecret` to exercise the insufficient-scopes error.
 */
export class MockConnectionManager implements ConnectionManager {
  private readonly connections = new Map<string, TenantConnection>();
  /** Lazy-init lookup: credential hash → fixture tenant (for scope simulation). */
  private fixtureByHash: Map<string, FixtureTenant> | null = null;

  private async getFixtureByHash(hash: string): Promise<FixtureTenant | undefined> {
    if (!this.fixtureByHash) {
      this.fixtureByHash = new Map();
      for (const t of FIXTURE_TENANTS) {
        const fh = await hashSecret(t.mockSecret);
        this.fixtureByHash.set(fh, t);
      }
    }
    return this.fixtureByHash.get(hash);
  }

  async connect(req: ConnectRequest): Promise<TenantConnection> {
    if (!req.locationId) throw new Error("locationId is required");
    if (!req.secret) throw new Error("secret is required");

    const hash = await hashSecret(req.secret);
    const masked = maskSecret(req.secret);
    const prior = this.connections.get(req.locationId);

    const now = new Date().toISOString();
    const record: TenantConnection = {
      id: req.locationId,
      locationId: req.locationId,
      locationName: req.locationName ?? prior?.locationName,
      status: "pending",
      credential: { masked, hash },
      scopes: [],
      missingScopes: [...REQUIRED_FF_SCOPES],
      connectedAt: now,
      rotatedAt: prior ? now : undefined,
    };
    this.connections.set(req.locationId, record);

    // Auto-test on connect so the admin gets immediate feedback.
    await this.test(req.locationId, { force: true });
    return this.connections.get(req.locationId)!;
  }

  async get(locationId: string): Promise<TenantConnection | null> {
    const c = this.connections.get(locationId);
    return c ? { ...c } : null;
  }

  async list(): Promise<TenantConnection[]> {
    // Mirrors EncryptedConnectionManager.list(): revoked connections are
    // excluded from the listing so the connect screen renders the same set in
    // mock and live mode (FUL-258 integration parity). A revoked location is
    // still reachable via get() for the "reconnect" path.
    return [...this.connections.values()]
      .filter((c) => c.status !== "revoked")
      .sort((a, b) => (a.connectedAt ?? "") < (b.connectedAt ?? "") ? 1 : -1)
      .map((c) => ({ ...c }));
  }

  async test(
    locationId: string,
    _opts?: ConnectionTestRequest,
  ): Promise<ConnectionTestResult> {
    const conn = this.connections.get(locationId);
    if (!conn) {
      return {
        ok: false,
        scopes: [],
        missingScopes: [...REQUIRED_FF_SCOPES],
        reason: "invalid_credential",
        message: "No connection found for this location. Connect first.",
        testedAt: new Date().toISOString(),
      };
    }

    // Simulate GHL token introspect / location lookup using the fixture map.
    // We compare by hash (the raw secret is already discarded).
    const fixture = await this.getFixtureByHash(conn.credential.hash);

    const scopesOk = fixture ? fixture.scopesOk : true;
    const locationName = fixture?.locationName ?? conn.locationName ?? `Location ${locationId.slice(-6)}`;
    const now = new Date().toISOString();

    const result: ConnectionTestResult = scopesOk
      ? {
          ok: true,
          scopes: [...REQUIRED_FF_SCOPES],
          missingScopes: [],
          locationName,
          testedAt: now,
        }
      : {
          ok: false,
          scopes: ["files.read"],
          missingScopes: REQUIRED_FF_SCOPES.filter((s) => s !== "files.read"),
          reason: "insufficient_scopes",
          message: "Credential is missing required scopes (contacts.write, tags.write, opportunities.write, workflows).",
          locationName,
          testedAt: now,
        };

    // Update the stored record's status + scope snapshot.
    const updated: TenantConnection = {
      ...conn,
      status: (result.ok ? "connected" : "error") as ConnectionStatus,
      scopes: result.scopes,
      missingScopes: result.missingScopes,
      locationName: result.locationName ?? conn.locationName,
      lastTestedAt: now,
      lastError: result.ok ? undefined : result.message,
    };
    this.connections.set(locationId, updated);

    return result;
  }

  async revoke(locationId: string): Promise<boolean> {
    const conn = this.connections.get(locationId);
    if (!conn) return false;
    this.connections.set(locationId, {
      ...conn,
      status: "revoked",
      revokedAt: new Date().toISOString(),
    });
    return true;
  }
}
